import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import cn from 'classnames';
import axios from '../../api/axios-sixtix';

import './Agenda.scss';
import { getTranslation } from '../../constants/translations';
import { getRegion } from '../../helpers/getRegion';

type Event = {
  bookingUrl: string;
  date: string;
  description: string | null;
  endDate: string;
  location: string;
  price: string;
  startDate: string;
  thumbnailUrl: string;
  title: string;
};

type EventsByMonth = { name: string; events: Event[] };

export const Agenda = () => {
  const t = getTranslation();
  const [eventsByMonths, setEventsByMonths] = useState<EventsByMonth[]>([]);
  const [currMonth, setCurrMonth] = useState<EventsByMonth>({
    name: '',
    events: [],
  });
  const domen = getRegion();
  const datesBlock = useRef<HTMLDivElement>(null);
  const eventBlock = useRef<HTMLLIElement>(null);
  const [isVerticalScrollUpBtnBlocked, setVerticalScrollUpBtnBlocked] =
    useState(true);
  const [isVerticalScrollDownBtnBlocked, setVerticalScrollDownBtnBlocked] =
    useState(false);

  const getData = async () => {
    let URL = 'EventPublicInfo/UpcomingEventsAgenda?results=100&page=0';

    if (domen !== 'com') {
      URL = `EventPublicInfo/UpcomingEventsAgenda?results=100&page=0&countryCode=${domen.toUpperCase()}`;
    }
    try {
      const { data } = await axios.get(URL);

      if (data.list.length === 0) return;

      const eventsByMonthsList: EventsByMonth[] = [];

      for (const event of data.list) {
        const monthName = moment(event.startDate).format('MMMM');
        const monthIdx = eventsByMonthsList.findIndex(
          (m) => m.name === monthName,
        );

        if (monthIdx !== -1) {
          eventsByMonthsList[monthIdx].events = [
            ...eventsByMonthsList[monthIdx].events,
            event,
          ];
        } else {
          eventsByMonthsList.push({
            name: monthName,
            events: [event],
          });
        }
      }

      setEventsByMonths([
        ...eventsByMonthsList,
        { name: 'HIDDEN', events: [] },
      ]);
      setCurrMonth(eventsByMonthsList[0]);
    } catch (error) {
      console.log(error);
    }
  };

  const changeMonth = (direction: 'prev' | 'next') => {
    const sixtix = document.getElementById('sixtix') as HTMLDivElement;
    const scrollBlock = document.getElementById(currMonth + '_events');
    scrollBlock?.scrollBy({
      top: -99999,
      left: 0,
    });

    const currMonthIdx = eventsByMonths.findIndex(
      (m) => m.name === currMonth?.name,
    );
    let nextMonth;

    switch (direction) {
      case 'prev':
        if (!currMonthIdx) return;
        nextMonth = eventsByMonths[currMonthIdx - 1];

        break;
      case 'next':
        if (currMonthIdx + 1 === eventsByMonths.length - 1) return;
        nextMonth = eventsByMonths[currMonthIdx + 1];
    }

    const targetBlock = document.getElementById(nextMonth.name);
    setCurrMonth(nextMonth);

    targetBlock?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
    sixtix.scrollLeft -= 100;
  };

  const scrollEvents = (direction: 'down' | 'up') => {
    const scrollBlock = document.getElementById(currMonth.name + '_events');
    const eventBlockHeight = eventBlock.current?.clientHeight as number;
    const gap = 20;
    const maxScrollToNextEvent = eventBlockHeight + gap;
    const currScroll = scrollBlock?.scrollTop as number;
    const scrollDownBy =
      (1 -
        (currScroll / maxScrollToNextEvent -
          Math.floor(currScroll / maxScrollToNextEvent))) *
      maxScrollToNextEvent;
    const scrollUpBy =
      (currScroll / maxScrollToNextEvent -
        Math.floor(currScroll / maxScrollToNextEvent)) *
        maxScrollToNextEvent || maxScrollToNextEvent;

    switch (direction) {
      case 'down':
        setVerticalScrollUpBtnBlocked(false);
        setVerticalScrollDownBtnBlocked(
          currMonth.events.length === currScroll / maxScrollToNextEvent + 2 + 1,
        );

        scrollBlock?.scrollBy({
          top: scrollDownBy,
          left: 0,
          behavior: 'smooth',
        });

        break;
      case 'up':
        setVerticalScrollUpBtnBlocked(
          scrollBlock?.scrollTop === maxScrollToNextEvent,
        );
        setVerticalScrollDownBtnBlocked(false);

        scrollBlock?.scrollBy({
          top: -scrollUpBy,
          left: 0,
          behavior: 'smooth',
        });
    }
  };

  useEffect(() => {
    const scrollBlock = document.getElementById(currMonth.name + '_events');

    if (scrollBlock) {
      scrollBlock.addEventListener('scroll', () => {
        if (datesBlock.current) {
          datesBlock.current.scrollTop = scrollBlock.scrollTop;
        }
      });
    }

    setVerticalScrollUpBtnBlocked(true);
    setVerticalScrollDownBtnBlocked(currMonth.events.length < 3);
  }, [currMonth]);

  useEffect(() => {
    getData();
  }, []);

  console.log(eventsByMonths);

  return (
    <section className="main__agenda agenda" id="agenda">
      <div className="_container">
        <div className="agenda__header">
          <h2 className="agenda__title title">{t.agenda.title}</h2>
        </div>

        {eventsByMonths.length === 0 ? (
          <div className="agenda__container agenda__container--empty-state">
            Nema dostupnih događaja
          </div>
        ) : (
          <div className="agenda__container">
            <div ref={datesBlock} className="agenda__dates-wrapper">
              <div className="agenda__dates">
                {currMonth.events.map((e) => (
                  <span key={e.startDate + e.title}>
                    {moment(e.startDate).format('DD-MM')}
                  </span>
                ))}
              </div>
            </div>
            <div className="agenda__static-container">
              <div className="agenda__horizontal-scroll-buttons-wrapper">
                <button
                  className={cn(
                    'agenda__horizontal-scroll-button agenda__horizontal-scroll-button--left',
                    {
                      'agenda__horizontal-scroll-button--disabled-left':
                        !eventsByMonths.findIndex(
                          (m) => m.name === currMonth?.name,
                        ),
                    },
                  )}
                  onClick={() => changeMonth('prev')}
                ></button>
                <button
                  className={cn('agenda__horizontal-scroll-button', {
                    'agenda__horizontal-scroll-button--disabled':
                      eventsByMonths.findIndex(
                        (m) => m.name === currMonth?.name,
                      ) +
                        1 ===
                      eventsByMonths.length - 1,
                  })}
                  onClick={() => changeMonth('next')}
                ></button>
              </div>

              <ul className="agenda__scroll-container">
                {eventsByMonths.map((month) => (
                  <li
                    key={month.name}
                    className="agenda__month"
                    id={month.name}
                  >
                    <div className="agenda__month-title">{month.name}</div>

                    <ul
                      className="agenda__events-container"
                      id={month.name + '_events'}
                    >
                      {month.events.map((event) => (
                        <li
                          className={cn('agenda__event event', {
                            'event--active': currMonth.events.some(
                              (e) => e.bookingUrl === event.bookingUrl,
                            ),
                          })}
                          id={month.name + event.title}
                          key={event.bookingUrl}
                          ref={eventBlock}
                        >
                          <div className="event__img">
                            <img src={event.thumbnailUrl} alt={event.title} />
                          </div>

                          <div className="event__info">
                            <div className="event__title">{event.title}</div>

                            <div className="event__location">
                              {event.location}
                            </div>
                            <div className="event__date">
                              {moment(event.startDate).format(
                                'DD.MM.YYYY HH:mm',
                              )}
                            </div>

                            <a
                              href={event.bookingUrl}
                              className="event__button button"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {t.agenda.tickets}
                            </a>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>

              <div className="agenda__vertical-scroll-buttons-wrapper">
                <button
                  className="agenda__vertical-scroll-button agenda__vertical-scroll-button--top"
                  onClick={() => scrollEvents('up')}
                  disabled={isVerticalScrollUpBtnBlocked}
                >
                  Up
                </button>
                <button
                  className="agenda__vertical-scroll-button agenda__vertical-scroll-button--bottom"
                  onClick={() => scrollEvents('down')}
                  disabled={isVerticalScrollDownBtnBlocked}
                >
                  Down
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
